import { create } from 'zustand';
import { getPartyAccreditationData, getAllAccreditationData, CATEGORIES_SOCIOPRO, AGE_TRANCHES, rniCandidatesList, ppsCandidatesList, pamCandidatesList, pjdCandidatesList, usfpCandidatesList, piCandidatesList } from './accreditationStore';
import { encodeWafPayload } from '@/utils/apiUtils';
import { moroccoRegions } from './parties';
import { getTranslatedProvince, getTranslatedRegion } from '@/utils/translations';

export interface CandidateData {
  id: string;
  partyAcronym?: string;
  firstName: string;
  lastName: string;
  firstNameAr?: string;
  lastNameAr?: string;
  photoUrl?: string;
  region: string;
  province: string;
  listType: "locale" | "regionale";
  bio?: string;
  facebook?: string;
  twitter?: string;
  instagram?: string;
  website?: string;
  genre?: string;
  dateNaissance?: string;
  categorieSociopro?: string;
  email?: string;
  whatsapp?: string;
}

interface CandidatesStore {
  candidates: CandidateData[];
  setCandidates: (candidates: CandidateData[]) => void;
  fetchPublicCandidates: () => Promise<CandidateData[]>;
  fetchCandidatesFromServer: (userId: string, partyAcronym: string) => Promise<void>;
  saveCandidate: (userId: string, partyAcronym: string, candidate: CandidateData) => Promise<void>;
  deleteCandidate: (userId: string, candidateId: string) => Promise<void>;
}

import { getApiUrl } from '@/utils/apiConfig';

export const useCandidatesStore = create<CandidatesStore>((set, get) => ({
  candidates: [],
  setCandidates: (candidates) => set({ candidates }),
  
  fetchPublicCandidates: async () => {
    let data: any[] = [];
    try {
      const res = await fetch(`${getApiUrl()}/candidates.php?public=1&t=${Date.now()}`);
      if (res.ok) {
        const fetched = await res.json();
        if (Array.isArray(fetched)) data = fetched;
      } else {
        console.warn("API Error, falling back to local data");
      }
    } catch (e) {
      console.error("Failed to fetch public candidates", e);
    }

    // Merge static RNI candidates to synchronize stores and dashboard/maps
    const formattedRNI = rniCandidatesList.map(c => ({
      id: `rni_${c.region}_${c.province}_${c.nom}`,
      firstName: c.prenom,
      lastName: c.nom,
      firstNameAr: c.prenomAr,
      lastNameAr: c.nomAr,
      partyAcronym: "RNI",
      region: c.region,
      province: c.province,
      listType: "locale" as const,
      genre: c.genre,
      source: "parti" as const,
      publishStatus: "published" as const
    }));

    const formattedPPS = ppsCandidatesList.map(c => ({
      id: `pps_${c.region}_${c.province}_${c.nom}`,
      firstName: c.prenom,
      lastName: c.nom,
      firstNameAr: c.prenomAr,
      lastNameAr: c.nomAr,
      partyAcronym: "PPS",
      region: c.region,
      province: c.province,
      listType: "locale" as const,
      genre: c.genre,
      source: "parti" as const,
      publishStatus: "published" as const
    }));

    const formattedPAM = pamCandidatesList.map(c => ({
      id: `pam_${c.region}_${c.province}_${c.nom}`,
      firstName: c.prenom,
      lastName: c.nom,
      firstNameAr: c.prenomAr,
      lastNameAr: c.nomAr,
      partyAcronym: "PAM",
      region: c.region,
      province: c.province,
      listType: "locale" as const,
      genre: c.genre,
      source: "parti" as const,
      publishStatus: "published" as const
    }));

    const formattedPJD = pjdCandidatesList.map(c => ({
      id: `pjd_${c.region}_${c.province}_${c.nom}`,
      firstName: c.prenom,
      lastName: c.nom,
      firstNameAr: c.prenomAr,
      lastNameAr: c.nomAr,
      partyAcronym: "PJD",
      region: c.region,
      province: c.province,
      listType: (c.province === "Régionale" ? "regionale" : "locale") as "locale" | "regionale",
      genre: c.genre,
      source: "parti" as const,
      publishStatus: "published" as const
    }));

    const formattedUSFP = usfpCandidatesList.map(c => ({
      id: `usfp_${c.region}_${c.province}_${c.nom}`,
      firstName: c.prenom,
      lastName: c.nom,
      firstNameAr: c.prenomAr,
      lastNameAr: c.nomAr,
      partyAcronym: "USFP",
      region: c.region,
      province: c.province,
      listType: "locale" as const,
      genre: c.genre,
      source: "parti" as const,
      publishStatus: "published" as const
    }));

    const formattedPI = piCandidatesList.map(c => ({
      id: `pi_${c.region}_${c.province}_${c.nom}`,
      firstName: c.prenom,
      lastName: c.nom,
      firstNameAr: c.prenomAr,
      lastNameAr: c.nomAr,
      partyAcronym: "PI",
      region: c.region,
      province: c.province,
      listType: "locale" as const,
      genre: c.genre,
      source: "parti" as const,
      publishStatus: "published" as const
    }));

    return [...formattedRNI, ...formattedPPS, ...formattedPAM, ...formattedPJD, ...formattedUSFP, ...formattedPI, ...data];
  },

  fetchCandidatesFromServer: async (userId, partyAcronym) => {
    if (!userId) return;
    
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const res = await fetch(`${getApiUrl()}/candidates.php?user_id=${userId}&t=${Date.now()}`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!res.ok) throw new Error("API Error");
      const data: CandidateData[] = await res.json();
      

      set({ candidates: data });
    } catch (e) {
      console.error("Failed to fetch candidates", e);
    }
  },

  saveCandidate: async (userId, partyAcronym, candidate) => {
    const { candidates } = get();
    const existingIdx = candidates.findIndex(c => c.id === candidate.id);
    const updated = [...candidates];
    if (existingIdx >= 0) updated[existingIdx] = candidate;
    else updated.push(candidate);
    
    set({ candidates: updated });
    
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      await fetch(`${API_URL}/candidates.php`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'text/plain',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: encodeWafPayload({ action: 'save', user_id: userId, party_acronym: partyAcronym, candidate })
      });
    } catch (e) { console.error(e); }
  },

  deleteCandidate: async (userId, candidateId) => {
    const { candidates } = get();
    set({ candidates: candidates.filter(c => c.id !== candidateId) });
    
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      await fetch(`${getApiUrl()}/candidates.php`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'text/plain',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: encodeWafPayload({ action: 'delete', id: candidateId, user_id: userId })
      });
    } catch (e) { console.error(e); }
  }
}));

// Backward compatibility exports
export const fetchPublicCandidates = () => useCandidatesStore.getState().fetchPublicCandidates();
export const fetchCandidatesFromServer = (u: string, p: string) => useCandidatesStore.getState().fetchCandidatesFromServer(u, p);
export const saveCandidate = (u: string, p: string, c: CandidateData) => useCandidatesStore.getState().saveCandidate(u, p, c);
export const deleteCandidate = (u: string, c: string) => useCandidatesStore.getState().deleteCandidate(u, c);

// ─── BAROMETER UTILS FOR CANDIDATES ───────────────────────────────────────────────────

export interface BarometerPartyStats {
  partyAcronym: string;
  total: number;
  accredited: number; // for candidates, total === accredited
  accreditedLocale: number;
  accreditedRegionale: number;
  en_attente: number; // Keep for interface compatibility, always 0
  non_depose: number; // Keep for interface compatibility, always 0
  refuse: number;     // Keep for interface compatibility, always 0
  hommes: number;
  femmes: number;
  jeunes: number;
  byTranche: Record<string, number>;
  bySociopro: Record<string, number>;
}

export interface BarometerRegionStats {
  region: string;
  total: number;
  accredited: number;
  en_attente: number; // Keep for interface compatibility
  hommes: number;
  femmes: number;
  jeunes: number;
  byTranche: Record<string, number>;
  bySociopro: Record<string, number>;
  byParty: Record<string, { total: number; accredited: number }>;
}

export interface BarometerProvinceStats {
  province: string;
  total: number;
  accredited: number;
  en_attente: number; // Keep for interface compatibility
  hommes: number;
  femmes: number;
  jeunes: number;
}

function computeAge(dateNaissance?: string) {
  if (!dateNaissance) return 40; // Default fallback for stats
  const birth = new Date(dateNaissance);
  const diff = Date.now() - birth.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
}

function ageToTranche(age: number) {
  if (age < 30) return "18–29";
  if (age < 40) return "30–39";
  if (age < 50) return "40–49";
  if (age < 60) return "50–59";
  return "60+";
}

export function computeCandidatesBarometerData(candidates: CandidateData[]) {
  // --- Par Parti ---
  const partyMap: Record<string, BarometerPartyStats> = {};
  
  candidates.forEach(c => {
    const p = c.partyAcronym || "IND";
    if (!partyMap[p]) {
      partyMap[p] = {
        partyAcronym: p,
        total: 0, accredited: 0, accreditedLocale: 0, accreditedRegionale: 0,
        en_attente: 0, non_depose: 0, refuse: 0,
        hommes: 0, femmes: 0, jeunes: 0,
        byTranche: Object.fromEntries(AGE_TRANCHES.map(t => [t, 0])),
        bySociopro: Object.fromEntries(CATEGORIES_SOCIOPRO.map(cat => [cat, 0]))
      };
    }
    
    const ps = partyMap[p];
    ps.total++;
    ps.accredited++;
    if (c.listType === "locale") ps.accreditedLocale++;
    if (c.listType === "regionale") ps.accreditedRegionale++;
    
    if (c.genre === "M" || c.genre === "Homme") ps.hommes++;
    if (c.genre === "F" || c.genre === "Femme") ps.femmes++;
    
    const age = computeAge(c.dateNaissance);
    if (age < 35) ps.jeunes++;
    
    const tranche = ageToTranche(age);
    if (ps.byTranche[tranche] !== undefined) ps.byTranche[tranche]++;
    
    if (c.categorieSociopro && ps.bySociopro[c.categorieSociopro] !== undefined) {
      ps.bySociopro[c.categorieSociopro]++;
    }
  });

  const byParty = Object.values(partyMap);

  // --- Par Région ---
  const regionMap: Record<string, BarometerRegionStats> = {};
  moroccoRegions.forEach(r => {
    regionMap[r.name] = {
      region: r.name,
      total: 0, accredited: 0, en_attente: 0,
      hommes: 0, femmes: 0, jeunes: 0,
      byTranche: Object.fromEntries(AGE_TRANCHES.map(t => [t, 0])),
      bySociopro: Object.fromEntries(CATEGORIES_SOCIOPRO.map(cat => [cat, 0])),
      byParty: {},
    };
  });

  candidates.forEach(c => {
    const rs = regionMap[c.region];
    if (!rs) return;
    
    rs.total++;
    rs.accredited++;
    
    if (c.genre === "M" || c.genre === "Homme") rs.hommes++;
    if (c.genre === "F" || c.genre === "Femme") rs.femmes++;
    
    const age = computeAge(c.dateNaissance);
    if (age < 35) rs.jeunes++;
    
    const tranche = ageToTranche(age);
    if (rs.byTranche[tranche] !== undefined) rs.byTranche[tranche]++;
    
    if (c.categorieSociopro && rs.bySociopro[c.categorieSociopro] !== undefined) {
      rs.bySociopro[c.categorieSociopro]++;
    }
    
    const p = c.partyAcronym || "IND";
    if (!rs.byParty[p]) rs.byParty[p] = { total: 0, accredited: 0 };
    rs.byParty[p].total++;
    rs.byParty[p].accredited++;
  });

  const byRegion = Object.values(regionMap);

  // --- Globaux ---
  const globalGenre = { hommes: 0, femmes: 0, total: 0 };
  const globalAge: Record<string, number> = Object.fromEntries(AGE_TRANCHES.map(t => [t, 0]));
  const globalSociopro: Record<string, number> = Object.fromEntries(CATEGORIES_SOCIOPRO.map(cat => [cat, 0]));
  let globalJeunes = 0;
  let globalTotal = 0;

  candidates.forEach(c => {
    globalTotal++;
    if (c.genre === "M" || c.genre === "Homme") globalGenre.hommes++;
    if (c.genre === "F" || c.genre === "Femme") globalGenre.femmes++;
    globalGenre.total++;
    
    const age = computeAge(c.dateNaissance);
    if (age < 35) globalJeunes++;
    const tranche = ageToTranche(age);
    if (globalAge[tranche] !== undefined) globalAge[tranche]++;
    
    if (c.categorieSociopro && globalSociopro[c.categorieSociopro] !== undefined) {
      globalSociopro[c.categorieSociopro]++;
    }
  });

  return { byParty, byRegion, globalGenre, globalAge, globalSociopro, globalJeunes, globalTotal };
}

export function computeCandidateProvinceStats(region: string, candidates: CandidateData[]): BarometerProvinceStats[] {
  const reg = moroccoRegions.find(r => r.name === region);
  if (!reg) return [];

  return reg.provinces.map(province => {
    const stats: BarometerProvinceStats = { province, total: 0, accredited: 0, en_attente: 0, hommes: 0, femmes: 0, jeunes: 0 };
    candidates.filter(c => c.region === region && c.province === province).forEach(c => {
      stats.total++;
      stats.accredited++;
      if (c.genre === "M" || c.genre === "Homme") stats.hommes++;
      if (c.genre === "F" || c.genre === "Femme") stats.femmes++;
      if (computeAge(c.dateNaissance) < 35) stats.jeunes++;
    });
    return stats;
  });
}
